// Stops added or removed on the timeline but not yet saved. A staged stop is an ordinary `Stop`;
// only its id prefix sets it apart.
import type { Stop } from "@/types/collections"

/** Saved stops carry a uuid, so this never collides; writers know there is no row yet. */
export const PENDING_STOP_PREFIX = "pending-stop-"

export const isPendingStop = (stopId: string) => stopId.startsWith(PENDING_STOP_PREFIX)

export const pendingStopId = (n: number) => `${PENDING_STOP_PREFIX}${n}`

export const resequenceStops = (stops: Stop[]): Stop[] =>
  [...stops]
    .sort((a, b) => a.plannedArrival.getTime() - b.plannedArrival.getTime())
    .map((stop, idx) => ({ ...stop, sequence: idx + 1 }))

/** Both args are local midnights; rounding (not truncating) survives a 23-hour DST day. */
export const dayOffsetFrom = (routeDate: Date, time: Date) => {
  const dayStart = new Date(time)
  dayStart.setHours(0, 0, 0, 0)
  return Math.round((dayStart.getTime() - routeDate.getTime()) / (1000 * 60 * 60 * 24))
}

/**
 * Day comes from the stop's own date, not the picker, since the timeline spans past midnight.
 * Departure before arrival rolls to the next day; equal times are left for the caller to reject.
 */
export const resolveStopTimes = (stopDate: string, arrivalTime: string, departureTime: string) => {
  const [year, month, day] = stopDate.split("-").map(Number)
  const [arrHours, arrMins] = arrivalTime.split(":").map(Number)
  const [depHours, depMins] = departureTime.split(":").map(Number)

  const arrival = new Date(year, month - 1, day, arrHours, arrMins, 0, 0)
  const departure = new Date(year, month - 1, day, depHours, depMins, 0, 0)
  if (departure.getTime() < arrival.getTime()) {
    departure.setDate(departure.getDate() + 1)
  }

  return { arrival, departure }
}
